import { useCallback, useEffect, useState } from 'react';

import { NativeResources, SortOrder } from '@ariksa/inventory-core';
import { useDispatch, useSelector } from 'react-redux';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { PageInfo } from 'components/Navigation';
import { AccessScope } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AccessScope';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

import { toApiScope } from './utils';

// Typing pauses this long before the search is sent.
const SEARCH_DELAY_MS = 400;

interface PrincipalFilters {
  resourceUuid: string;
  scope: AccessScope;
  // An empty type or privilege means no filter.
  principalType: string;
  privilege: string;
  // What the search box shows, and what was last sent.
  searchInput: string;
  search: string;
  sortOrder: SortOrder;
  pageInfo: PageInfo;
}

const initialFilters = (resourceUuid: string): PrincipalFilters => ({
  resourceUuid,
  scope: 'all',
  principalType: '',
  privilege: '',
  searchInput: '',
  search: '',
  sortOrder: SortOrder.Asc,
  pageInfo: INIT_PAGE_INFO,
});

/**
 * One page of the Principals tab from the paginated access-by-identity API,
 * with the filters, search and page sent with it.
 */
export const usePrincipalsPage = () => {
  const dispatch = useDispatch();
  const {
    resourceUuid,
    resourceAccountId,
    resourceType,
    canAccessSummary,
  } = useSelector(selectActiveResource);
  const { accessByIdentityPage } = canAccessSummary;

  const [state, setState] = useState(() => initialFilters(resourceUuid));
  // Another resource starts from the defaults. Resetting during render rather
  // than in an effect means no request goes out with the last one's filters.
  const filters =
    state.resourceUuid === resourceUuid ? state : initialFilters(resourceUuid);
  if (filters !== state) setState(filters);

  const {
    scope,
    principalType,
    privilege,
    search,
    sortOrder,
    pageInfo,
  } = filters;

  // Any filter, search or sort change starts again from the first page. The
  // table reports its sort on every render, so a value that hasn't changed
  // must keep the same state; otherwise each render sends another request.
  const update = useCallback(
    (changes: Partial<PrincipalFilters>) =>
      setState(current => {
        const keys = Object.keys(changes) as (keyof PrincipalFilters)[];
        if (keys.every(key => current[key] === changes[key])) return current;
        return {
          ...current,
          ...changes,
          pageInfo: { ...current.pageInfo, page_number: 1 },
        };
      }),
    [],
  );

  const setPageInfo = useCallback(
    (info: PageInfo) =>
      setState(current =>
        current.pageInfo.page_number === info.page_number &&
        current.pageInfo.page_size === info.page_size
          ? current
          : { ...current, pageInfo: info },
      ),
    [],
  );

  const setSearchInput = useCallback(
    (value: string) =>
      setState(current => ({ ...current, searchInput: value ?? '' })),
    [],
  );

  // Enter sends the search at once; otherwise it goes after a pause.
  const applySearch = useCallback(
    () =>
      setState(current => {
        const next = current.searchInput.trim();
        if (next === current.search) return current;
        return {
          ...current,
          search: next,
          pageInfo: { ...current.pageInfo, page_number: 1 },
        };
      }),
    [],
  );

  useEffect(() => {
    if (filters.searchInput.trim() === filters.search) return;
    const timer = setTimeout(applySearch, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [filters.searchInput, filters.search, applySearch]);

  /**
   * A role that is itself listed is found by name, across every scope, type
   * and privilege, since it may sit outside the current filters.
   */
  const showRole = useCallback(
    (name: string) =>
      update({
        scope: 'all',
        principalType: '',
        privilege: '',
        searchInput: name,
        search: name,
      }),
    [update],
  );

  useEffect(() => {
    if (!resourceUuid) return;
    dispatch(
      actions.getAccessByIdentityPage({
        q: {
          uuid: resourceUuid,
          accountId: resourceAccountId,
          nativeResourceType: resourceType as NativeResources,
          scope: toApiScope(scope),
          principalType: principalType || undefined,
          privilege: privilege || undefined,
          search: search || undefined,
          sortOrder,
          page: pageInfo.page_number,
          size: pageInfo.page_size,
        },
      }),
    );
  }, [
    dispatch,
    resourceUuid,
    resourceAccountId,
    resourceType,
    scope,
    principalType,
    privilege,
    search,
    sortOrder,
    pageInfo,
  ]);

  return {
    page: accessByIdentityPage,
    filters,
    update,
    setPageInfo,
    setSearchInput,
    applySearch,
    showRole,
  };
};
